import { API_BASE_URL, authFetchJson } from '../apiClient.ts';

export type MailProvider = 'OUTLOOK' | 'GMAIL';

export type TouchScanStatus = 'running' | 'ok' | 'error' | 'reconnect_needed';

export type MailAccount = {
  id: number;
  provider: MailProvider;
  emailAddress: string;
  displayName: string | null;
  scopes: string;
  lastScanAt: string | null;
  lastScanStatus: string | null;
  lastScanError: string | null;
  lastScanScanned: number | null;
  lastScanSuggested: number | null;
  lastScanSummary: string | null;
  lastScanLookbackDays: number | null;
  lastContactSyncAt: string | null;
  lastContactSyncStatus: string | null;
  lastContactSyncError: string | null;
  lastContactSyncFound: number | null;
  lastContactSyncSummary: string | null;
  // Relationship tracking: the header-only scan behind relationship scores.
  touchScanEnabled: boolean;
  lastTouchScanAt: string | null;
  lastTouchScanStatus: TouchScanStatus | null;
  lastTouchScanError: string | null;
  lastTouchScanSummary: string | null;
  touchCount: number;
  scanRunning: boolean;
  contactSyncRunning: boolean;
  touchScanRunning: boolean;
  canSyncContacts: boolean;
  canSyncPeople: boolean;
  // Relationship tracking counts meetings only once the calendar is granted
  canReadCalendar: boolean;
  isActive: boolean;
  needsReconnect: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ScanResult = {
  accountId: number;
  status: 'ok' | 'reconnect_needed';
  scanned: number;
  automated: number;
  noSignal: number;
  candidates: number;
  extracted: number;
  suggested: number;
  alreadyKnown: number;
  skipped: number;
  errors: number;
  complete: boolean;
  extractionCapHit: boolean;
  message: string;
};

export const providerLabels: Record<MailProvider, string> = {
  OUTLOOK: 'Outlook',
  GMAIL: 'Gmail'
};

// A provider card only shows once the API side is configured for it.
export const providerEnabled: Record<MailProvider, boolean> = {
  OUTLOOK: import.meta.env.VITE_OUTLOOK_ENABLED === 'true',
  GMAIL: import.meta.env.VITE_GMAIL_ENABLED === 'true'
};

export const fetchMailAccounts = async (): Promise<MailAccount[]> => {
  const { json } = await authFetchJson(`${API_BASE_URL}/integrations/mail/accounts`);
  return json as MailAccount[];
};

// The API owns the whole OAuth exchange. The browser asks it for an authorize
// URL and follows the redirect; nothing provider-specific lives here.
export const startMailConnect = async (provider: MailProvider) => {
  const { json } = await authFetchJson(
    `${API_BASE_URL}/integrations/${provider.toLowerCase()}/connect`
  );
  const url = (json as { authorizeUrl?: string }).authorizeUrl;
  if (!url) {
    throw new Error('The API did not return an authorize URL');
  }
  window.location.assign(url);
};

export const disconnectMailAccount = async (id: number) => {
  await authFetchJson(`${API_BASE_URL}/integrations/mail/accounts/${id}`, { method: 'DELETE' });
};

export type ScanStart = { accountId: number; status: 'running' };

export const SCAN_WINDOWS = [7, 15, 30] as const;
export type ScanWindow = (typeof SCAN_WINDOWS)[number];
export const DEFAULT_SCAN_WINDOW: ScanWindow = 7;
const SCAN_WINDOW_KEY = 'forge.scanWindow';

export const loadScanWindow = (): ScanWindow => {
  try {
    const raw = Number(localStorage.getItem(SCAN_WINDOW_KEY));
    return (SCAN_WINDOWS as readonly number[]).includes(raw)
      ? (raw as ScanWindow)
      : DEFAULT_SCAN_WINDOW;
  } catch {
    return DEFAULT_SCAN_WINDOW;
  }
};

export const saveScanWindow = (days: ScanWindow) => {
  try {
    localStorage.setItem(SCAN_WINDOW_KEY, String(days));
  } catch {
    // storage unavailable; the pick still applies for this page
  }
};

// Starts the scan and returns at once; the mailbox row carries progress
// (lastScanScanned, lastScanSuggested) and the outcome (lastScanStatus).
export const scanMailAccount = async (id: number, days: ScanWindow): Promise<ScanStart> => {
  const { json } = await authFetchJson(
    `${API_BASE_URL}/integrations/mail/accounts/${id}/scan?days=${days}`,
    { method: 'POST' }
  );
  return json as ScanStart;
};

export const describeScanOutcome = (account: MailAccount): string => {
  const scanned = account.lastScanScanned ?? 0;
  const suggested = account.lastScanSuggested ?? 0;
  if (account.lastScanStatus === 'error') {
    return `${account.emailAddress}: scan failed after ${scanned} messages. ${account.lastScanError ?? ''}`.trim();
  }
  if (account.needsReconnect) {
    return `${account.emailAddress}: the mailbox needs to be reconnected in Settings.`;
  }
  if (account.lastScanSummary) return `${account.emailAddress}: ${account.lastScanSummary}`;
  return `${account.emailAddress}: scanned ${scanned} messages, found ${suggested} new ${suggested === 1 ? 'person' : 'people'}.`;
};

export type ContactSyncStart = {
  accountId: number;
  status: 'running' | 'reconnect_needed';
  message?: string;
};

// Starts the sync and returns at once; the mailbox row carries the count so
// far (lastContactSyncFound) and the outcome (lastContactSyncStatus).
export const syncMailContacts = async (id: number): Promise<ContactSyncStart> => {
  const { json } = await authFetchJson(
    `${API_BASE_URL}/integrations/mail/accounts/${id}/sync-contacts`,
    { method: 'POST' }
  );
  return json as ContactSyncStart;
};

// Starts the relationship scan and returns at once; touchScanRunning and
// touchCount on the mailbox row carry progress. 409 when tracking is off, the
// mailbox needs reconnecting, or a scan is already running.
export const scanMailTouches = async (id: number): Promise<ScanStart> => {
  const { json } = await authFetchJson(
    `${API_BASE_URL}/integrations/mail/accounts/${id}/scan-touches`,
    { method: 'POST' }
  );
  return json as ScanStart;
};

// Turning tracking on starts the 12-month backfill in the background.
export const setTouchTracking = async (id: number, enabled: boolean): Promise<MailAccount> => {
  const { json } = await authFetchJson(
    `${API_BASE_URL}/integrations/mail/accounts/${id}/touch-tracking`,
    { method: 'PUT', body: JSON.stringify({ enabled }) }
  );
  return json as MailAccount;
};

// Deletes the mailbox's relationship history and turns tracking off.
export const deleteMailTouches = async (
  id: number
): Promise<{ deleted: number; account: MailAccount }> => {
  const { json } = await authFetchJson(`${API_BASE_URL}/integrations/mail/accounts/${id}/touches`, {
    method: 'DELETE'
  });
  return json as { deleted: number; account: MailAccount };
};

export const formatDateTime = (raw?: string | null): string => {
  if (!raw) return '';
  const date = new Date(raw);
  return isNaN(date.getTime()) ? '' : date.toLocaleString();
};

export function describeContactSync(account: MailAccount, reconnectHint: string): string {
  if (!account.canSyncContacts) return reconnectHint;
  if (account.contactSyncRunning) {
    return `Syncing now: ${account.lastContactSyncFound ?? 0} queued so far`;
  }
  if (!account.lastContactSyncAt) return 'Contacts not synced yet';
  if (account.lastContactSyncStatus === 'error') {
    return `Contact sync failed ${formatDateTime(account.lastContactSyncAt)}: ${account.lastContactSyncError ?? 'unknown error'}`;
  }
  return `Contacts synced ${formatDateTime(account.lastContactSyncAt)}: ${account.lastContactSyncSummary ?? `${account.lastContactSyncFound ?? 0} queued`}`;
}
